import { useAppContext } from '../../AppContext';
import { Panels } from '../Consts';
import {
  ContentCard, Div, Footer, Group, Panel, PullToRefresh, RichCell, Separator, Skeleton, Text,
} from '@vkontakte/vkui';
import React, { useEffect } from 'react';
import { InviteBlockComponent } from './components/InviteBlockComponent';
import { AddPaymentButtonsComponent } from './components/AddPaymentButtonsComponent';
import { ItemsBlockComponent } from './components/ItemsBlockComponent';
import CreditsBlockComponent from './components/CreditsBlockComponent';
import { ErrorLoadComponent } from '../components/ErrorLoadComponent';
import {
  getRouteCreatePayment,
  getRouteCreateRepayment, getRouteEventSettings, getRouteScanReceiptsList,
} from '../../route';
import { PlatformPanelHeader } from '../components/PlatformPanelHeader';
import { checkEventStatusForCreatePayment, checkEventStatusForCreateRepayment } from '../../helpers/helpers';

const CURRENT_PANEL = Panels.EVENT_INFO;

export const clearCache = (eventId, cache) => {
  cache.set(CURRENT_PANEL + '_' + eventId, undefined);
  cache.set('ItemsBlockComponent_' + eventId, undefined);
}

export const getEvent = (eventId, context, clearCache = false) => {
  return new Promise((resolve, reject) => {
    if (!clearCache) {
      const data = context.cache.get(CURRENT_PANEL + '_' + eventId);
      if (data) {
        return resolve({
          users: data.users,
          credits: data.credits,
          event: data.event,
        });
      }
    }

    context.init().then(() => {
      context.httpClient()
        .getEventV4(eventId)
        .then((response) => {
          const users = {};
          response.data.users.forEach((user) => {
            users[user.id] = user;
          });

          context.cache.set(CURRENT_PANEL + '_' + eventId, {
            users: users,
            credits: response.data.credits,
            event: response.data.event,
          });

          return resolve({
            users: users,
            credits: response.data.credits,
            event: response.data.event,
          });
        }).catch((error) => {
          return reject(error);
        });
    }).catch((error) => {
      return reject(error);
    });
  });
}

const SceletonPanel = () => {
  return (<Panel nav={CURRENT_PANEL} className={'SceletonPanel'}>
    <PlatformPanelHeader
      text={<Skeleton width={170} />}
    />
    <Div className={'AddPaymentButtons__oneButton'}>
      <Skeleton width="100%" height={36} />
    </Div>
    <Div className="EventImage">
      <ContentCard
        className="EventImage__img"
        header={<Skeleton width={70} />}
        caption={<Skeleton width={170} height={36} />}
      />
    </Div>
    <Group mode="plain">
      {[1,2,3,4,5,6,7,8,9,10].map((i) => (
        <div
          key={'div' + i}
        >
          <RichCell
            key={i}
            multiline
            caption={<Skeleton width={70} />}
            after={<div className={'PaymentRow__rightBlock'}>
              <div><Skeleton width={70} heigth={17}/></div>
              <div><Skeleton width={70} height={10} /></div>
            </div>}
            actions={''}
          >
            <Skeleton width={30} height={30} borderRadius={'50%'}/>
          </RichCell>
          <Separator/>
        </div>
      ))}
    </Group>
  </Panel>);
}

const ShowEventPanel = () => {
  const context = useAppContext();
  const params = context.router.getParams();

  const [event, setEvent] = React.useState(null);
  const [credits, setCredits] = React.useState([]);
  const [members, setMembers] = React.useState({});
  //items
  const [itemsLoading, setItemsLoading] = React.useState(false);
  const [items, setItems] = React.useState([]);
  const [itemsUsers, setItemsUsers] = React.useState({});
  const [itemsTotalCount, setItemsTotalCount] = React.useState(0);
  const [error, setError] = React.useState(false);

  const [isRefreshFetching, setIsRefreshFetching] = React.useState(false);

  const loadEventItems = (eventId, offset) => {
    setItemsLoading(true);
    context.httpClient()
      .getEventItems(eventId, offset)
      .then((response) => {
        const _items = offset < 1 ? [] : items;
        response.data.items.forEach((item) => {
          _items.push(item);
        });
        response.data.members.forEach((user) => {
          itemsUsers[user.id] = user;
        });

        setItemsUsers({...itemsUsers});
        setItemsTotalCount(response.data.count);
        setItems(_items);

        context.cache.set('ItemsBlockComponent_' + eventId, {
          items: _items,
          itemsUsers: {...itemsUsers},
          itemsTotalCount: response.data.count,
        });
      })
      .catch((error) => {
        context.showError(error);
      })
      .finally(() => {
        setItemsLoading(false);
      });
  }

  const onRefresh = () => {
    setIsRefreshFetching(true);
    getEvent(params.eventId, context, true)
      .then(({users, credits, event}) => {
        setMembers(users);
        setCredits(credits);
        setEvent(event);

        setItems([]);
        setItemsUsers({});
        loadEventItems(params.eventId, 0);
        setError(null);
        setIsRefreshFetching(false);
      })
      .catch((error) => {
        setError(error);
        setIsRefreshFetching(false);
        console.error(error);
      });
  }

  const fetchItems = (id) => {
    const itemsCache = context.cache.get('ItemsBlockComponent_' + id);
    if (itemsCache) {
      setItemsUsers(itemsCache.itemsUsers);
      setItemsTotalCount(itemsCache.itemsTotalCount);
      setItems(itemsCache.items);
    } else {
      context.init(true)
        .then(() => {
          loadEventItems(id, 0);
        })
        .catch((error) => context.showError(error));
    }
  }

  useEffect(() => {
    if (!params.eventId) {
      return;
    }

    getEvent(params.eventId, context)
      .then(({users, credits, event}) => {
        setMembers(users);
        setCredits(credits);
        setEvent(event);
        fetchItems(params.eventId);
        setError(null);
        context.spinner(false);

        if (context.store.isShowConfirmShareLink) {
          context.store.setIsShowConfirmShareLink(false);

          const shareCallback = (value) => {
            if (value === true) {
              context.getBridge().shareInvite(event.id, event.invite, event.invite_link, context);
            }
          }

          context.confirm(
            context.lang().getKey(CURRENT_PANEL, 'share_event_link_after_crate_confirm_text'),
            shareCallback,
            context.lang().getKey(CURRENT_PANEL, 'share_event_link_after_crate_confirm_ok_label'),
            context.lang().getKey(CURRENT_PANEL, 'share_event_link_after_crate_confirm_cancel_label')
          );
        }

      })
      .catch((error) => {
        setError(error);
        console.error(error);
      });
  }, []);

  if (error) {
    return (<ErrorLoadComponent
      onFetch={onRefresh}
      error={error}
    />)
  }

  if (event === null) {
    return (<SceletonPanel />);
  }

  const openCreateItemPanel = () => {
    if (checkEventStatusForCreatePayment(context, event)) {
      context.router.push(...getRouteCreatePayment(params.eventId, 'event_info'));
    }
  }

  const openSettings = () => {
    context.router.push(...getRouteEventSettings(params.eventId))
  }

  return (<Panel nav={CURRENT_PANEL}>
    <PlatformPanelHeader
      text={event.name}
    />
    {context.snackbar}
    <PullToRefresh onRefresh={onRefresh} isFetching={isRefreshFetching} style={{paddingBottom: 40}}>
      <AddPaymentButtonsComponent
        onCreatePayment={openCreateItemPanel}
        addPaymentLabel={context.lang().getKey(CURRENT_PANEL, 'add_payment')}
        scanReceiptLabel={context.lang().getKey(CURRENT_PANEL, 'scan_receipt_label')}
        isReceiptScanner={context.store.isEnabledScanReceipt}
        event={event}
      />
      <InviteBlockComponent
        eventId={event.id}
        eventName={event.name}
        eventImage={event.image?.src}
        eventInviteCode={event.invite}
        eventInviteLink={event.invite_link}
        buttonLabel={context.lang().getKey(CURRENT_PANEL, 'send_invite_button')}
        settingsButtonLabel={context.lang().getKey(CURRENT_PANEL, 'settings_button')}
        settingsButtonOnClick={() => openSettings()}
        scanReceiptsListLabel={context.lang().getKey(CURRENT_PANEL, 'scan_receipts_list_label')}
        scanReceiptsListButtonOnClick={() => context.router.push(...getRouteScanReceiptsList(params.eventId))}
      />
      <ItemsBlockComponent
        eventId={Number(params.eventId)}
        items={items}
        users={itemsUsers}
        itemsTotalCount={itemsTotalCount}
        loading={itemsLoading}
        onClickLoadMore={() => loadEventItems(params.eventId, items.length)}
        eventMembers={Object.values(members)}
        errorTextNotFoundItems={context.lang().getKey(CURRENT_PANEL, 'search_not_found_items')}
        loadItemsButtonLabel={context.lang().getKey(CURRENT_PANEL, 'load_items_button')}
      />
      { items.length > 0 && (<Div style={{position: 'relative'}}>
          <Text weight="2" style={{marginBottom: 16, float: 'left', width: '50%'}}>
            {context.lang().getKey(CURRENT_PANEL, 'itog_header')}
          </Text>
          <Text weight="2" style={{marginBottom: 16, float: 'right', width: '50%', textAlign: 'right'}}>
            {context.lang().formatPrice(event.total_amount)}
          </Text>
        </Div>)
      }
      {
        credits.length > 0 ? (<Group>
          <Text level="1" weight="1" style={{marginBottom: 16, textAlign: 'center', fontSize: 24}}>
            {context.lang().getKey(CURRENT_PANEL, 'itog_text_1')}
          </Text>
          <Text level="3" weight="3" style={{textAlign: 'center', color: '#6D7885', fontSize: 13}}>
            {context.lang().getKey(CURRENT_PANEL, 'itog_text_2')}
          </Text>
        </Group>) : <Footer>{context.lang().getKey(CURRENT_PANEL, 'payments_list_empty')}</Footer>
      }
      <CreditsBlockComponent
        panel={CURRENT_PANEL}
        emptyListLabel={context.lang().getKey(CURRENT_PANEL, 'empty_list_label')}
        credits={credits}
        members={members}
        onClickAddRepayment={(creditorId, userId) => {
          if (checkEventStatusForCreateRepayment(context, event)) {
            context.router.push(...getRouteCreateRepayment(params.eventId, userId, creditorId));
          }
        }}
      />
    </PullToRefresh>
  </Panel>);
}

export default ShowEventPanel;
